/**
 * NoticeEventBus — Lightweight, type-safe pub/sub for notice events.
 *
 * Any component can fire events into the bus. Registered handlers
 * react to events by creating, updating, or dismissing notices
 * via the noticeStore.
 *
 * Naming convention:
 *   - Event types are UPPER_SNAKE_CASE describing the domain event.
 *   - The bus does NOT call the backend directly — handlers update
 *     the store, which in turn may call the API.
 */

// ─── Event Types ────────────────────────────────────────────

export type NoticeEventType =
  | 'AI_TASK_STARTED'
  | 'AI_TASK_COMPLETED'
  | 'AI_TASK_FAILED'
  | 'CHANNEL_SYNC_STARTED'
  | 'CHANNEL_SYNC_COMPLETED'
  | 'GENERIC_NOTICE_SHOW'
  | 'DISMISS_NOTICE';

export interface NoticeEvent {
  type: NoticeEventType;
  payload: Record<string, any>;
}

type NoticeEventHandler = (event: NoticeEvent) => void;

// ─── Bus Implementation ─────────────────────────────────────

class NoticeEventBusImpl {
  private _handlers = new Map<NoticeEventType, Set<NoticeEventHandler>>();
  private _globalHandlers = new Set<NoticeEventHandler>();

  /**
   * Subscribe to a specific event type.
   * Returns an unsubscribe function.
   */
  subscribe(eventType: NoticeEventType, handler: NoticeEventHandler): () => void {
    if (!this._handlers.has(eventType)) {
      this._handlers.set(eventType, new Set());
    }
    this._handlers.get(eventType)!.add(handler);

    return () => {
      this._handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Subscribe to ALL event types (wildcard).
   * Returns an unsubscribe function.
   */
  subscribeAll(handler: NoticeEventHandler): () => void {
    this._globalHandlers.add(handler);
    return () => {
      this._globalHandlers.delete(handler);
    };
  }

  /**
   * Fire an event. All subscribed handlers will be called synchronously.
   */
  fire(event: NoticeEvent): void {
    // Type-specific handlers
    const handlers = this._handlers.get(event.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(event);
        } catch (err) {
          console.error(`[NoticeEventBus] Handler error for ${event.type}:`, err);
        }
      });
    }

    // Global handlers
    this._globalHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (err) {
        console.error(`[NoticeEventBus] Global handler error for ${event.type}:`, err);
      }
    });
  }

  /**
   * Remove all handlers (for cleanup on unmount).
   */
  clear(): void {
    this._handlers.clear();
    this._globalHandlers.clear();
  }
}

/**
 * Singleton instance — imported and used across the app.
 */
export const noticeEventBus = new NoticeEventBusImpl();
